import { ActivatedRoute, Router } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ViewId, ViewPresetService } from '@services/view-preset.service';

import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgTypePillCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-type-pill-cell-renderer/ag-type-pill-cell-renderer.component';
import { AgMetricCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-metric-cell-renderer/ag-metric-cell-renderer.component';
import { AgDateTimeFilterComponent } from '../../../ag-grid/components/ag-date-time-filter/ag-date-time-filter.component';
import { AppState } from 'src/app/reducers';
import { ColDef } from 'ag-grid-community';
import { CompanyService } from '../../services/company.service';
import { CoreService } from '../../services/core.service';
import { GeneralDynamicAgGridComponent } from 'src/app/ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { redirectToObject } from 'src/app/shared/utils/object-utils';

@Component({
  selector: 'app-feeds',
  templateUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.html',
  styleUrls: [
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.scss',
  ],
  standalone: false,
})
export class FeedsComponent extends GeneralDynamicAgGridComponent {
  viewId: ViewId = ViewId.Feeds;

  tableTitle = this.translateService.instant('grid.title.feeds');
  tableIcon: IconProp = ['fas', 'rss'];

  public components: {
    [p: string]: any;
  } = {
    agDateInput: AgDateTimeFilterComponent,
  };

  protected generalGridClassName: string = 'feeds-grid';

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected companyService: CompanyService,
    protected translateService: TranslateService,
    protected logger: LoggerService,
    protected viewPresetService: ViewPresetService,
    protected coreService: CoreService,
    protected editService: ObjectEditorService,
    protected toastService: ToastService,
  ) {
    super(
      store,
      route,
      cdr,
      logger,
      router,
      viewPresetService,
      toastService,
      translateService,
    );
  }

  protected fetchAgGridData(request: any): Observable<any> {
    return this.coreService.fetchUserFeedsAgGrid(request);
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        headerName: this.translateService.instant('grid.column.name'),
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          objectId: 'object_id',
          objectClassId: 'object_class_id',
          objectTypeId: 'object_type_id',
          lifecycleStatus: 'lifecycle_status',
          onClick: (data) => {
            redirectToObject(data, this.router, true);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        ...this.createNomenclatureColumnDef({
          headerNameKey: 'grid.column.type',
          fieldKey: 'object_type.id',
          fieldValue: 'object_type.title',
          nomenclatureName: 'query_type',
          otherFilterParams: {
            defaultToNothingSelected: true,
          },
        }),
        cellRenderer: AgTypePillCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.description'),
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.dimensions-count',
        ),
        field: 'dimensions_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.alerts-count'),
        field: 'alerts_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedules-count',
        ),
        field: 'export_schedules_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.algorithms-count',
        ),
        field: 'algorithms_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          icon: ['fas', 'folder'],
          objectId: 'workspace.id',
          objectClassId: 'workspace.object_class_id',
          lifecycleStatus: 'workspace.lifecycle_status',
          onClick: (data) => {
            if (data.can_view && data.can_edit) {
              redirectToObject(data.workspace, this.router, true);
            } else {
              this.toastService.showInfo(
                this.translateService.instant(
                  'feeds.error.you-do-not-have-permissions-or-object-not-active',
                ),
                this.translateService.instant('feeds.error.not-available'),
              );
            }
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.owner'),
        field: 'owner',
        filter: 'agTextColumnFilter',
        maxWidth: 280,
        sortable: true,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'owner.name',
          avatarKey: 'owner.avatar_url',
          objectId: 'owner.id',
          objectClassId: 'owner.object_class_id',
          lifecycleStatus: 'owner.lifecycle_status',
          onClick: (data) => {
            this.router.navigate(['admin', 'home', data.user.id]);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.time-created'),
        field: 'time_created.absolute',
        filter: 'agDateColumnFilter',
        maxWidth: 180,
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
        filterParams: this.filterParams,
      },
      {
        headerName: this.translateService.instant('grid.column.time-updated'),
        field: 'time_updated.absolute',
        filter: 'agDateColumnFilter',
        maxWidth: 180,
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
        filterParams: this.filterParams,
      },
    ];
  }
}
